import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ContactFilters } from '../../../config/api';
import ContactFiltersComponent from './ContactFilters';

interface ContactsControlsBarProps {
  currentView: 'table' | 'card' | 'kanban';
  onViewChange: (view: 'table' | 'card' | 'kanban') => void;
  filters: ContactFilters;
  onFilterChange: (filters: Partial<ContactFilters>) => void;
}

const ContactsControlsBar: React.FC<ContactsControlsBarProps> = ({
  currentView,
  onViewChange,
  filters,
  onFilterChange
}) => {
  const navigate = useNavigate();

  const viewButtons = [
    {
      id: 'table' as const,
      label: 'Table View',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    },
    {
      id: 'card' as const,
      label: 'Card View',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    {
      id: 'kanban' as const,
      label: 'Kanban View',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    }
  ];

  return (
    <div className="px-6 py-6">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-6 py-4">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            {/* Left side - Filters */}
            <div className="flex flex-col md:flex-row md:items-center gap-4 flex-1">
              {/* Filters */}
              <div className="flex-shrink-0">
                <ContactFiltersComponent 
                  filters={filters}
                  onChange={onFilterChange}
                />
              </div>
            </div>

            {/* Right side - View toggles and Add button */}
            <div className="flex items-center justify-between md:justify-start gap-4 flex-shrink-0">
              {/* View toggle buttons */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {viewButtons.map((button) => (
                  <button
                    key={button.id}
                    onClick={() => onViewChange(button.id)}
                    className={`p-2 rounded-md transition-colors ${
                      currentView === button.id
                        ? 'bg-white dark:bg-gray-600 text-lex-accent shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    title={button.label}
                  >
                    {button.icon}
                  </button>
                ))}
              </div>

              {/* Add Contact button */}
              <button
                onClick={() => navigate('/contacts/new')}
                className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg font-medium transition-colors flex items-center gap-2 h-10"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Contact</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsControlsBar; 